import {
  formatArea,
  formatLength,
  formatMaterialQty,
  formatMoney,
  formatNumber,
  round,
} from './calculator'
import type { DormerInputs, DormerResult, UnitSystem } from './calculator'

type RGB = [number, number, number]

const INK: RGB = [15, 34, 54]
const BLUE: RGB = [43, 87, 132]
const STEEL: RGB = [95, 108, 121]
const RULE: RGB = [213, 220, 227]
const SIGNAL: RGB = [217, 142, 10]

/**
 * Builds and downloads a formatted PDF of the calculator results.
 * jsPDF is imported on demand so it never lands in the main page bundle.
 */
export async function saveResultsPdf(
  input: DormerInputs,
  result: DormerResult,
  unit: UnitSystem,
): Promise<void> {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true })

  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const M = 46
  const contentW = pageW - M * 2
  const bottomLimit = pageH - 84
  const generated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  let y = 0

  const ensure = (need: number) => {
    if (y + need > bottomLimit) {
      doc.addPage()
      y = 64
      doc.setDrawColor(...RULE)
      doc.setLineWidth(0.7)
      doc.line(M, 48, pageW - M, 48)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8.5)
      doc.setTextColor(...STEEL)
      doc.text('DORMER ESTIMATE  |  DORMERCALCULATOR.COM', M, 40)
    }
  }

  const sectionTitle = (label: string) => {
    ensure(46)
    y += 20
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(...BLUE)
    doc.text(label.toUpperCase(), M, y)
    y += 7
    doc.setDrawColor(...RULE)
    doc.setLineWidth(0.8)
    doc.line(M, y, pageW - M, y)
    y += 17
  }

  const row = (label: string, value: string, detail?: string, bold = false) => {
    const labelLines = doc.splitTextToSize(label, contentW - 160) as string[]
    const blockHeight = labelLines.length * 13 + (detail ? 12 : 0) + 4
    ensure(blockHeight + 6)
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(10.5)
    doc.setTextColor(...INK)
    doc.text(labelLines, M, y)
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.text(value, pageW - M, y, { align: 'right' })
    y += labelLines.length * 13
    if (detail) {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8.5)
      doc.setTextColor(...STEEL)
      doc.text(doc.splitTextToSize(detail, contentW - 90) as string[], M, y)
      y += 12
    }
    y += 4
  }

  // ---- Header band -------------------------------------------------------
  doc.setFillColor(...INK)
  doc.rect(0, 0, pageW, 96, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(21)
  doc.setTextColor(255, 255, 255)
  doc.text('Dormer Estimate', M, 44)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9.5)
  doc.setTextColor(143, 178, 214)
  doc.text(`dormercalculator.com  |  ${generated}`, M, 64)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(19)
  doc.setTextColor(245, 166, 35)
  doc.text(formatMoney(result.total), pageW - M, 44, { align: 'right' })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(185, 208, 232)
  doc.text(
    `${formatMoney(result.low)} - ${formatMoney(result.high)} likely range`,
    pageW - M,
    64,
    { align: 'right' },
  )

  y = 124

  // ---- Project details ---------------------------------------------------
  sectionTitle('Project details')
  const lenUnit = unit === 'metric' ? 'm' : 'ft'
  row('Width', `${formatNumber(input.width, 2)} ${lenUnit}`)
  row('Depth', `${formatNumber(input.depth, 2)} ${lenUnit}`)
  row('Front wall height', `${formatNumber(input.height, 2)} ${lenUnit}`)
  row('Roof pitch', `${round(input.pitch, 1)}:12  (${formatNumber(result.pitchDegrees, 1)}\u00B0)`)
  row('Material and finish', result.material.label, result.material.description)

  // ---- Areas -------------------------------------------------------------
  sectionTitle('Areas and geometry')
  const stats: [string, string][] = [
    ['Floor area', formatArea(result.floorArea, unit)],
    ['Roof area', formatArea(result.roofArea, unit)],
    ['Wall area', formatArea(result.wallArea, unit)],
    ['Roof rise', formatLength(result.rise, unit)],
    ['Rafter length', formatLength(result.slopeLength, unit)],
    ['Cost per sq ft of floor', formatMoney(result.costPerSqFtFloor)],
  ]
  const colW = contentW / 2
  for (let i = 0; i < stats.length; i += 2) {
    ensure(34)
    stats.slice(i, i + 2).forEach(([label, value], idx) => {
      const x = M + idx * colW
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(...STEEL)
      doc.text(label.toUpperCase(), x, y)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11.5)
      doc.setTextColor(...INK)
      doc.text(value, x, y + 15)
    })
    y += 34
  }

  // ---- Materials ---------------------------------------------------------
  sectionTitle('Materials needed')
  result.materials.forEach(m => {
    row(m.name, formatMaterialQty(m, unit), m.detail)
  })

  // ---- Cost breakdown ----------------------------------------------------
  sectionTitle('Cost breakdown')
  result.costs.forEach(c => row(c.label, formatMoney(c.amount)))
  row('Contingency (10%)', formatMoney(result.contingency))
  row('Design and permits (5%)', formatMoney(result.permits))

  ensure(46)
  y += 6
  doc.setDrawColor(...RULE)
  doc.line(M, y, pageW - M, y)
  y += 18
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.setTextColor(...INK)
  doc.text('Estimated total', M, y)
  doc.setTextColor(...SIGNAL)
  doc.text(formatMoney(result.total), pageW - M, y, { align: 'right' })
  y += 16
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...STEEL)
  doc.text(
    `Likely range ${formatMoney(result.low)} to ${formatMoney(result.high)}`,
    pageW - M,
    y,
    { align: 'right' },
  )

  // ---- Footers -----------------------------------------------------------
  const totalPages = doc.getNumberOfPages()
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p)
    doc.setDrawColor(...RULE)
    doc.setLineWidth(0.7)
    doc.line(M, pageH - 62, pageW - M, pageH - 62)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(...STEEL)
    doc.text(
      doc.splitTextToSize(
        'Planning estimate only, produced from average US material and labour rates. Not a quotation or professional advice. Confirm dimensions and prices with a licensed contractor before ordering.',
        contentW - 70,
      ) as string[],
      M,
      pageH - 50,
    )
    doc.text(`Page ${p} of ${totalPages}`, pageW - M, pageH - 50, { align: 'right' })
  }

  const stamp = new Date().toISOString().slice(0, 10)
  doc.save(`dormer-estimate-${stamp}.pdf`)
}
